import React from 'react'
import { User } from '../../../types'
import { 
  User as UserIcon, 
  Mail, 
  Calendar, 
  DollarSign, 
  Building2,
  Edit,
  Key,
  MoreVertical,
  CheckCircle,
  XCircle,
  Award
} from 'lucide-react'
import LevelBadge from '../../../components/common/LevelBadge'

interface UserCardProps {
  user: User
  onEdit: (user: User) => void
  onSetHourlyRate: (user: User) => void
  onSetLevel: (user: User) => void
  onManageBankInfo: (user: User) => void
  onResetPassword: (user: User) => void
  onToggleStatus: (userId: string, isActive: boolean) => void
}

const UserCard: React.FC<UserCardProps> = ({
  user,
  onEdit,
  onSetHourlyRate,
  onSetLevel,
  onManageBankInfo,
  onResetPassword,
  onToggleStatus
}) => {
  const getRoleBadge = (role: string) => {
    const badges = {
      ADMIN: 'badge-admin bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300',
      MANAGER: 'badge-manager bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300',
      EMPLOYEE: 'badge-employee bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300'
    }
    
    const roleNames = {
      ADMIN: '管理員',
      MANAGER: '經理',
      EMPLOYEE: '員工'
    }

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${badges[role as keyof typeof badges]}`}>
        {roleNames[role as keyof typeof roleNames]}
      </span>
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-TW')
  }

  const maskAccountNumber = (accountNumber: string) => {
    if (accountNumber.length <= 4) return accountNumber
    return '*'.repeat(accountNumber.length - 4) + accountNumber.slice(-4)
  }

  return (
    <div className="card hover:shadow-medium transition-all duration-300">
      {/* 用戶基本資訊 */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            {/* 頭像 */}
            <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center">
              {user.avatar ? (
                <img 
                  src={user.avatar} 
                  alt={user.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <UserIcon className="w-6 h-6 text-primary-600" />
              )}
            </div>
            
            {/* 姓名和信箱 */}
            <div>
              <h3 className="font-semibold text-secondary-800 text-lg">{user.name}</h3>
              <div className="flex items-center space-x-1 text-secondary-600 text-sm">
                <Mail className="w-4 h-4" />
                <span>{user.email}</span>
              </div>
            </div>
          </div>

          {/* 狀態、角色和職等 */}
          <div className="flex flex-col items-end space-y-2">
            <div className="flex items-center space-x-2">
              {user.isActive ? (
                <div className="flex items-center space-x-1 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-xs font-medium">啟用</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1 text-gray-500">
                  <XCircle className="w-4 h-4" />
                  <span className="text-xs font-medium">停用</span>
                </div>
              )}
              {getRoleBadge(user.role)}
            </div>
            {/* 職等徽章 */}
            <div className="flex items-center">
              <LevelBadge level={user.level} showLabel size="sm" />
            </div>
          </div>
        </div>

        {/* 詳細資訊 */}
        <div className="space-y-3 mb-6">
          {/* 時薪資訊 */}
          <div className="flex items-center space-x-2 text-secondary-700">
            <DollarSign className="w-4 h-4 text-primary-600" />
            <span className="text-sm">
              基本時薪: {user.hourlyRate ? `${user.currency} ${user.hourlyRate}/小時` : '未設定'}
            </span>
          </div>

          {/* 加入時間 */}
          <div className="flex items-center space-x-2 text-secondary-700">
            <Calendar className="w-4 h-4 text-primary-600" />
            <span className="text-sm">加入時間: {formatDate(user.createdAt)}</span>
          </div>

          {/* 銀行資訊 */}
          <div className="flex items-center space-x-2 text-secondary-700">
            <Building2 className="w-4 h-4 text-primary-600" />
            <span className="text-sm">
              銀行資訊: {user.bankInfo ? (
                <span className="text-green-600">
                  {user.bankInfo.bankName} - {user.bankInfo.branchName} 
                  <br />
                  <span className="text-xs text-secondary-500">
                    帳號: {maskAccountNumber(user.bankInfo.accountNumber)} ({user.bankInfo.accountHolder})
                  </span>
                </span>
              ) : (
                <span className="text-orange-600">未設定</span>
              )}
            </span>
          </div>
        </div>

        {/* 操作按鈕 */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onSetHourlyRate(user)}
            className="btn-ghost text-sm flex items-center space-x-1"
          >
            <DollarSign className="w-4 h-4" />
            <span>設定時薪</span>
          </button>

          <button
            onClick={() => onSetLevel(user)}
            className="btn-ghost text-sm flex items-center space-x-1"
          >
            <Award className="w-4 h-4" />
            <span>調整職等</span>
          </button>

          <button
            onClick={() => onManageBankInfo(user)}
            className="btn-ghost text-sm flex items-center space-x-1"
          >
            <Building2 className="w-4 h-4" />
            <span>銀行資訊</span>
          </button>

          <button
            onClick={() => onResetPassword(user)}
            className="btn-ghost text-sm flex items-center space-x-1"
          >
            <Key className="w-4 h-4" />
            <span>重設密碼</span>
          </button>

          <button
            onClick={() => onEdit(user)}
            className="btn-ghost text-sm flex items-center space-x-1"
          >
            <Edit className="w-4 h-4" />
            <span>編輯</span>
          </button>

          {/* 更多選項 */}
          <div className="relative group">
            <button className="btn-ghost text-sm flex items-center space-x-1">
              <MoreVertical className="w-4 h-4" />
              <span>更多</span>
            </button>
            
            {/* 下拉選單 */}
            <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-primary-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              <div className="py-2">
                <button
                  onClick={() => onToggleStatus(user.id, !user.isActive)}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    user.isActive 
                      ? 'text-red-600 hover:bg-red-50' 
                      : 'text-green-600 hover:bg-green-50'
                  }`}
                >
                  {user.isActive ? '停用帳號' : '啟用帳號'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserCard
